import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { ScreenType } from "../components/WalkieTalkie";

// --- Types ---
export interface ChannelType {
  id: string;
  name: string;
  mhz: number;
}

export interface UseTalkieWalkieReturn<T extends string | undefined> {
  // Screens
  activeScreen: ScreenType;
  setActiveScreen: (screen: ScreenType) => void;
  resetScreen: (screen?: ScreenType) => void;

  // Channels
  channels: ChannelType[];
  favoriteChannels: ChannelType[];
  activeChannel: ChannelType | null;
  connectToChannel: (channel: ChannelType) => void;
  disconnectFromChannel: () => void;
  addChannelToFavorites: (channel: ChannelType) => void;
  removeChannelFromFavorites: (channel: ChannelType) => void;

  // Unified disconnect
  disconnect: () => void;
}

// --- Context ---
const TalkieWalkieContext = createContext<
  UseTalkieWalkieReturn<any> | undefined
>(undefined);

// --- Provider ---
export const TalkieWalkieProvider = ({ children }: { children: ReactNode }) => {
  // --- Screens ---
  const [activeScreen, setActiveScreenState] = useState<ScreenType | undefined>(
    "radio-connect"
  );

  const setActiveScreen = useCallback((screen: ScreenType) => {
    setActiveScreenState(screen);
  }, []);

  const resetScreen = useCallback(() => {
    setActiveScreenState("radio-connect");
  }, []);

  // --- Channels ---
  const defaultChannels: ChannelType[] = [
    { id: "1", name: "General", mhz: 101 },
    { id: "2", name: "Team", mhz: 102 },
    { id: "3", name: "Random", mhz: 103 },
  ];

  const [channels /*setChannels */] = useState<ChannelType[]>(defaultChannels);
  const [favoriteChannels, setFavoriteChannels] = useState<ChannelType[]>([]);
  const [activeChannel, setActiveChannel] = useState<ChannelType | null>(null);

  // load saved channels
  useEffect(() => {
    const saved = localStorage.getItem("favoriteChannels");
    if (saved) {
      const favIds: string[] = JSON.parse(saved);
      const favChannels = channels.filter((c) => favIds.includes(c.id));
      setFavoriteChannels(favChannels);
    }
  }, [channels]);

  const saveFavoriteChannels = (favChannels: ChannelType[]) => {
    setFavoriteChannels(favChannels);
    localStorage.setItem(
      "favoriteChannels",
      JSON.stringify(favChannels.map((c) => c.id))
    );
  };

  const connectToChannel = useCallback((channel: ChannelType) => {
    setActiveChannel(channel);
    setActiveScreen("menu");
    console.log(`Connected to channel: ${channel.name}`);
  }, []);

  const disconnectFromChannel = useCallback(() => {
    console.log(`Disconnected from channel: ${activeChannel?.name}`);
    setActiveChannel(null);
  }, [activeChannel]); // eslint-disable-line

  const addChannelToFavorites = useCallback(
    (channel: ChannelType) => {
      if (!favoriteChannels.find((c) => c.id === channel.id)) {
        saveFavoriteChannels([...favoriteChannels, channel]);
      }
    },
    [favoriteChannels]
  );

  const removeChannelFromFavorites = useCallback(
    (channel: ChannelType) => {
      saveFavoriteChannels(favoriteChannels.filter((c) => c.id !== channel.id));
    },
    [favoriteChannels]
  );

  // --- Unified Disconnect ---
  const disconnect = useCallback(() => {
    resetScreen(); // reset active screen
    disconnectFromChannel(); // disconnect active channel
  }, [resetScreen, disconnectFromChannel]);

  return (
    <TalkieWalkieContext.Provider
      value={{
        activeScreen: activeScreen as ScreenType,
        setActiveScreen,

        resetScreen,
        channels,
        favoriteChannels,
        activeChannel,
        connectToChannel,
        disconnectFromChannel,
        addChannelToFavorites,
        removeChannelFromFavorites,
        disconnect,
      }}
    >
      {children}
    </TalkieWalkieContext.Provider>
  );
};

// --- Hook ---
export const useTalkieWalkie = <T extends string | undefined>(
  initialActiveScreen?: ScreenType
): UseTalkieWalkieReturn<T> => {
  const context = useContext(TalkieWalkieContext);
  if (!context) {
    throw new Error(
      "useTalkieWalkie must be used within a TalkieWalkieProvider"
    );
  }

  // Set initial screen if provided
  useEffect(() => {
    if (
      initialActiveScreen !== undefined &&
      context.activeScreen === undefined
    ) {
      context.setActiveScreen(initialActiveScreen);
    }
  }, [initialActiveScreen, context]);

  return context;
};
